import logoPath from './bps_logo.webp'
import posthog from 'posthog-js'
import { Elm } from './Main.elm'
import registerServiceWorker from './registerServiceWorker'
import './FullCalendar'
import './EasyMDE'
require('dotenv').config({ path: './.env' })

// A workaround to avoid app crash when Google Translate extension is used.
// Must be loaded before the app is initialized.
//
// https://insurello.se/how-we-made-elm-and-google-translate-work-together/
HTMLFontElement.prototype.replaceData =
  function replaceData (_0, _1, string) {
    this.parentNode.replaceChild(
      document.createTextNode(string),
      this
    )
  }

//
// Config
//
const config = {
  productionUrl: 'my.budapestschool.org',
  logAppNameBackend: 'my_bps',
  logAppNameClient: '[MYBPS]',
  gaMeasurementCode: 'G-9BYV5RPY4B',
  posthogKey: process.env.ELM_APP_POSTHOG_KEY || 'phc_Lh5LhSUz99Sb861x5ThH0kSXOq21TuzDoqqsSUvSUCw',
  posthogHost: process.env.ELM_APP_POSTHOG_HOST || 'https://eu.i.posthog.com'
}

//
// Init app
//
const lastRequestedUrl = sessionStorage.getItem('lastRequestedUrl')
const app = Elm.Main.init({
  node: document.getElementById('root'),
  flags: {
    lastRequestedUrl,
    auth0Domain: process.env.ELM_APP_AUTH0_DOMAIN,
    auth0ClientId: process.env.ELM_APP_AUTH0_CLIENT_ID,
    auth0Callback: process.env.ELM_APP_AUTH0_REDIRECT_URL,
    apiBase: process.env.ELM_APP_BACKEND_API,
    logAppName: config.logAppNameBackend,
    logMaxLength: 1024,
    logoPath,
    buildRev: process.env.ELM_APP_BUILD_REVISION || 'dev'
  }
})

//
// Set up Google Analytics tracking
//
if (window.location.hostname === config.productionUrl) {
  window.dataLayer = window.dataLayer || []
  window.gtag = function () { window.dataLayer.push(arguments) }
  window.gtag('js', new Date())
}

//
// Set up PostHog analytics
//
if (window.location.hostname === config.productionUrl && config.posthogKey) {
  posthog.init(config.posthogKey, {
    api_host: config.posthogHost,
    capture_pageview: false
  })
}

//
// Ports
//
app.ports.saveLastRequestedUrl.subscribe(function (data) {
  console.debug(config.logAppNameClient, 'storing last requested URL', data)
  sessionStorage.setItem('lastRequestedUrl', data)
})

app.ports.consoleError.subscribe(function (data) {
  console.error(config.logAppNameClient, data)
})

app.ports.consoleInfo.subscribe(function (data) {
  console.info(config.logAppNameClient, data)
})

// Sends a page view to Google Analytics and PostHog
app.ports.sendPageView.subscribe(function (data) {
  if (typeof window.gtag === 'function') {
    window.gtag('config', config.gaMeasurementCode, {
      page_path: data.pagePath,
      page_title: data.pageTitle
    })
  }

  if (window.location.hostname === config.productionUrl && config.posthogKey) {
    posthog.capture('$pageview', {
      $current_url: window.location.href,
      $title: data.pageTitle,
      page_path: data.pagePath
    })
  }
})

// Identify the user in PostHog when Elm sends identity
app.ports.posthogIdentify.subscribe(function (data) {
  if (
    window.location.hostname === config.productionUrl &&
    config.posthogKey
  ) {
    posthog.identify(data.distinctId, {
      $email: data.email, $first_name: data.firstName, $last_name: data.lastName
    })
  }
})

// Reset PostHog identity on logout
app.ports.posthogReset.subscribe(function () {
  if (
    window.location.hostname === config.productionUrl &&
    config.posthogKey
  ) {
    posthog.reset()
  }
})

// Scrolls to given element
app.ports.scrollToElement.subscribe(function (selector) {
  const el = document.querySelector(selector)
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
})

// Set the website's language through setting the "lang" property on the root
// "html" element.
app.ports.setPageLanguage.subscribe(function (lang) {
  document.querySelector('html').lang = lang
})

// Find cookie with given name and return its value
const getCookie = function (name) {
  const cookies = document.cookie.split('; ')
  return cookies
    .map((cookieStr) => cookieStr.split('='))
    .filter((cookie) => cookie[0] === name)
    .map((cookie) => cookie[1])
    .reduce((acc, val) => val, null)
}

// Read CSRF token from cookie
app.ports.fetchCsrfToken.subscribe(function (str) {
  const value = getCookie('csrftoken')
  if (value === null) {
    app.ports.csrfTokenReciever.send('')
  } else {
    app.ports.csrfTokenReciever.send(value)
  }
})

// Switch user (impersonate) with credentials
app.ports.switchUserRequest.subscribe(function (data) {
  console.log(config.logAppNameClient, 'Sending switchUser request', {
    email: data.email,
    reason: data.reason,
    apiBase: data.apiBase,
    hasCsrfToken: !!data.csrfToken
  })

  fetch(`${data.apiBase}/su/`, {
    method: 'POST',
    credentials: 'include', // THIS IS THE KEY FIX - sends cookies cross-origin
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': data.csrfToken
    },
    body: JSON.stringify({
      email: data.email,
      reason: data.reason
    })
  })
    .then(response => {
      console.log(config.logAppNameClient, 'switchUser response:', {
        status: response.status,
        statusText: response.statusText,
        headers: {
          'set-cookie': response.headers.get('set-cookie'),
          'content-type': response.headers.get('content-type')
        }
      })

      // Log all cookies after the response
      console.log(config.logAppNameClient, 'Cookies after switchUser:', document.cookie)

      if (response.ok) {
        // Extract sessionid from response body and set it as a cookie
        response.json().then(data => {
          console.log(config.logAppNameClient, 'switchUser SUCCESS - response data:', data)

          if (data.sessionid) {
            // Clear JWT token first
            document.cookie = 'jwt_id_token=; path=/; domain=.budapestschool.org; expires=Thu, 01 Jan 1970 00:00:00 GMT; secure; samesite=None'
            console.log(config.logAppNameClient, 'JWT token cleared')

            // Set the sessionid cookie (without HttpOnly since JS can't set that)
            // The backend's Set-Cookie might be blocked cross-origin, so we set it from JS
            const maxAge = 1209600 // 14 days in seconds
            document.cookie = `sessionid=${data.sessionid}; path=/; domain=.budapestschool.org; max-age=${maxAge}; secure; samesite=None`
            console.log(config.logAppNameClient, 'Session cookie set from JS, reloading page...')

            // Reload the page - now with the session cookie set
            window.location.href = '/'
          } else {
            console.error(config.logAppNameClient, 'No sessionid in response!')
            app.ports.switchUserResponse.send({
              success: false,
              error: 'No session ID received',
              statusCode: response.status
            })
          }
        }).catch(err => {
          console.error(config.logAppNameClient, 'Failed to parse response:', err)
          app.ports.switchUserResponse.send({
            success: false,
            error: 'Failed to parse response',
            statusCode: response.status
          })
        })
      } else {
        response.text().then(text => {
          console.error(config.logAppNameClient, 'switchUser FAILED:', response.status, text)
          app.ports.switchUserResponse.send({
            success: false,
            error: text || `HTTP ${response.status}`,
            statusCode: response.status
          })
        }).catch(() => {
          app.ports.switchUserResponse.send({
            success: false,
            error: `HTTP ${response.status}`,
            statusCode: response.status
          })
        })
      }
    })
    .catch(error => {
      console.error(config.logAppNameClient, 'switchUser NETWORK ERROR:', error)
      app.ports.switchUserResponse.send({
        success: false,
        error: error.message || 'Network error',
        statusCode: null
      })
    })
})

// Load the manifest.json
app.ports.loadManifest.subscribe((manifestUrl) => {
  const manifestRel = document.getElementById('webmanifest')
  manifestRel.setAttribute('href', manifestUrl)
})

// Remove the manifest.json reference
app.ports.resetManifest.subscribe(() => {
  const manifestRel = document.getElementById('webmanifest')
  manifestRel.removeAttribute('href')
})

//
// Service worker
//
registerServiceWorker(config, app.ports)
